import qs from 'qs';
import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import $utils from '@/utils';
import { executeBulkAction as executeBulk } from "@/utils/bulk";
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';

const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

function buildProductsBulkEditFilters(state, filtered = false) {
    if (filtered) {
        const filters = Object.assign({}, state.products_filters);
        delete filters.available_for_sale;
        filters.owned = filters.owned ?? true;
        delete filters.limit;
        delete filters.offset;
        return filters;
    }
    return {
        owned: true,
    };
}

const state = {
    products: [],
    products_count: 0,
    products_filters: { limit: 20, ordering: 'name', owned: true },
    products_loading: false,
    products_list_loaded: false,
    products_loading_errors: null,
    products_cancel_source: null,
    selected_items: [],
    bulk_action_loading: false,
};

const mutations = {
    updateProducts(state, products) {
        state.products = products;
        // Replace selected_items instances with newly fetched products based on id field
        state.selected_items = state.selected_items.map((selected_item) => {
            return products.find((product) => product.id === selected_item.id) || selected_item;
        });
    },
    updateProductsFilters(state, products_filters) {
        // Drop a previously persisted sale-only filter so the products page
        // lists every product, including those with sales disabled.
        delete products_filters.available_for_sale;
        state.products_filters = products_filters;
        saveFiltersToStorage(getStorageKey('products', 'default'), state.products_filters);
    },
    updateProductsCount(state, products_count) {
        state.products_count = products_count;
    },
    updateProductsLoading(state, loading) {
        state.products_loading = loading;
    },
    updateProductsListLoaded(state, loaded) {
        state.products_list_loaded = loaded;
    },
    updateProductsLoadingErrors(state, errors) {
        state.products_loading_errors = errors;
    },
    updateProductsCancelSource(state, source) {
        state.products_cancel_source = source;
    },
    updateSelectedItems(state, items) {
        state.selected_items = items;
    },
    updateBulkActionLoading(state, loading) {
        state.bulk_action_loading = loading;
    },
};

const actions = {

    async bulkAction({ commit, dispatch, state }, params) {
        commit("updateBulkActionLoading", true);
        try {
            const result = await executeBulk({
                bulkActionUrl: "/api/products/bulk/",
                action: params.action,
                instances: state.selected_items.map(item => item.id),
                payload: params.payload || {},
                context: params.context,
                getIdFn: params.getIdFn,
                successMessage: params.successMessage,
            });
            const items = state.selected_items;
            dispatch('fetchProducts');
            dispatch("session/fetchStats", null, { root: true });
            return { ...result, items };
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateBulkActionLoading", false);
        }
    },


    exportProductsAsCSV({ state }, params) {
        let filters = {};
        if (params.filtered) {
            filters = Object.assign({}, state.products_filters);
            filters.offset = null;
        }
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;
        delete filters.available_for_sale;
        if (params.filtered) {
            filters.owned = filters.owned ?? true;
        }
        
        $utils.downloadFile({
            url: "/api/products/",
            params: filters,
        }, $utils.getFilename('products'));
    },

    getProductsBulkEditFilters({ state }, { filtered = false } = {}) {
        return buildProductsBulkEditFilters(state, filtered);
    },

    async fetchProducts({ commit, state, dispatch, rootGetters }) {
        if (rootGetters['session/current_user_permissions'].indexOf("core.view_product") === -1) {
            commit('updateProductsLoading', false);
            commit('updateProductsListLoaded', true);
            return;
        }

        if (state.products_cancel_source) {
            state.products_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateProductsCancelSource', cancelSource);
        // products_list_loaded is NOT reset here: it only tracks the initial
        // load (see resetProductsList). Refreshes rely on products_loading so
        // the table stays visible with its loading state.
        commit('updateProductsLoading', true);
        commit('updateProductsLoadingErrors', null);

        const filters = loadFiltersFromStorage(getStorageKey('products', 'default'), state.products_filters);
        commit('updateProductsFilters', filters);

        const finishFetch = () => {
            if (state.products_cancel_source !== cancelSource) {
                return;
            }
            commit('updateProductsCancelSource', null);
            commit('updateProductsLoading', false);
            commit('updateProductsListLoaded', true);
        };

        try {
            const response = await axios.get('/api/products/', {
                params: filters,
                cancelToken: cancelSource.token,
            });
            if (state.products_cancel_source !== cancelSource) {
                return;
            }
            commit('updateProducts', response.data.results);
            commit('updateProductsCount', response.data.count);
            finishFetch();
            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            if (state.products_cancel_source !== cancelSource) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateProductsLoadingErrors', error.details);
            finishFetch();
            throw error;
        }
    },

    resetProductsList({ commit }) {
        commit('updateProductsLoading', true);
        commit('updateProductsListLoaded', false);
        commit('updateProducts', []);
        commit('updateProductsCount', 0);
        commit('updateProductsLoadingErrors', null);
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};